import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { Shell } from "../components/Shell";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle2, XCircle, Clock, Users, FileText, Cpu, LifeBuoy, Shield, IndianRupee, AlertTriangle } from "lucide-react";

const TABS = ["overview", "payments", "customers", "analytics", "ai", "support", "privacy"];
const TAB_LABEL = { overview: "Overview", payments: "Payments", customers: "Customers", analytics: "Analytics", ai: "AI Usage", support: "Support", privacy: "Privacy" };

export default function Admin({ tab: initial = "overview" }) {
    const [tab, setTab] = useState(initial);
    return (
        <Shell eyebrow="Owner" title="Admin dashboard" max="max-w-6xl">
            <div className="flex gap-1.5 flex-wrap mb-8 border-b border-charcoal/10 pb-3">
                {TABS.map((x) => (
                    <button key={x} data-testid={`admin-tab-${x}`} onClick={() => setTab(x)}
                        className={`px-4 py-2 rounded-full text-sm ${tab === x ? "bg-forest text-ivory font-medium" : "hover:bg-charcoal/5 text-charcoal/70"}`}>{TAB_LABEL[x]}</button>
                ))}
            </div>
            {tab === "overview" && <Overview />}
            {tab === "payments" && <Payments />}
            {tab === "customers" && <Customers />}
            {tab === "analytics" && <Analytics />}
            {tab === "ai" && <AIUsage />}
            {tab === "support" && <Support />}
            {tab === "privacy" && <Privacy />}
        </Shell>
    );
}

const Stat = ({ icon: Icon, label, value, accent }) => (
    <div className="card-soft p-5">
        <div className="flex items-center gap-2 text-charcoal/55 text-xs">{Icon && <Icon size={14} />} {label}</div>
        <p className={`mt-1 text-2xl font-serif ${accent || "text-forest"}`}>{value}</p>
    </div>
);

function Overview() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div className="space-y-6">
            {(!d.ai_configured || !d.email_configured || !d.upi_configured) && (
                <div className="rounded-xl bg-gold/10 border border-gold/30 p-4 text-sm text-charcoal/80 flex gap-2" data-testid="config-warning">
                    <AlertTriangle size={16} className="text-gold mt-0.5 shrink-0" />
                    <span>Configuration: AI {d.ai_configured ? "✓" : "✗ not set"} · Email {d.email_configured ? "✓" : "✗ not set"} · UPI {d.upi_configured ? "✓" : "✗ not set"}. Set missing values in backend/.env.</span>
                </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={Users} label="Total users" value={d.total_users} />
                <Stat icon={Users} label="New (7d)" value={d.new_users_7d} />
                <Stat icon={FileText} label="Assessments started" value={d.assessments_started} />
                <Stat icon={FileText} label="Free completed" value={d.free_completed} />
                <Stat icon={Clock} label="Pending payments" value={d.pending_payments} accent="text-gold" />
                <Stat icon={CheckCircle2} label="Approved" value={d.approved_orders} />
                <Stat icon={XCircle} label="Rejected" value={d.rejected_orders} />
                <Stat icon={FileText} label="Reports generated" value={d.reports_generated} />
                <Stat icon={IndianRupee} label="Gross collected" value={`₹${d.gross_collected}`} />
                <Stat icon={IndianRupee} label="Refunds" value={`₹${d.refund_total}`} />
                <Stat icon={IndianRupee} label="Net collections" value={`₹${d.net_collections}`} />
                <Stat icon={IndianRupee} label="Bank settlements" value={d.bank_settlements == null ? "N/A" : d.bank_settlements} accent="text-charcoal/50" />
                <Stat icon={Cpu} label="AI requests" value={d.ai_requests} />
                <Stat icon={Cpu} label="AI errors" value={d.ai_errors} accent={d.ai_errors ? "text-destructive" : "text-forest"} />
                <Stat icon={Cpu} label="AI cost est." value={d.ai_cost_estimate == null ? "N/A" : d.ai_cost_estimate} accent="text-charcoal/50" />
                <Stat icon={LifeBuoy} label="Support requests" value={d.support_requests} />
            </div>
            <p className="text-xs text-charcoal/50">Gross and net are computed from approved orders × ₹{d.price}. "Bank settlements" stays N/A until verified settlement data is wired — it is never inferred from approvals.</p>
        </div>
    );
}

function Payments() {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [q, setQ] = useState("");
    const [approveOrder, setApproveOrder] = useState(null);
    const [rejectOrder, setRejectOrder] = useState(null);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get("/admin/payments", { params: { status: filter, q: q || undefined } });
        setOrders(data.orders);
    }, [filter, q]);
    useEffect(() => { load(); }, [load]);

    const doApprove = async () => {
        setBusy(true);
        try { const { data } = await api.post(`/admin/payments/${approveOrder.order_id}/approve`); toast.success(data.customer_emailed ? "Approved & customer emailed." : "Approved."); setApproveOrder(null); load(); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
        setBusy(false);
    };
    const doReject = async () => {
        if (reason.trim().length < 3) { toast.error("Please give a reason."); return; }
        setBusy(true);
        try { await api.post(`/admin/payments/${rejectOrder.order_id}/reject`, { reason: reason.trim() }); toast.success("Rejected."); setRejectOrder(null); setReason(""); load(); }
        catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
        setBusy(false);
    };

    const badge = (s) => ({ PENDING: "bg-charcoal/10 text-charcoal", SUBMITTED: "bg-gold/15 text-gold", APPROVED: "bg-forest/10 text-forest", REJECTED: "bg-destructive/10 text-destructive", REFUNDED: "bg-lavender/15 text-lavender" }[s] || "bg-charcoal/10");

    return (
        <div>
            <div className="flex flex-wrap gap-2 mb-4">
                {["pending", "approved", "rejected", "all"].map((f) => (
                    <button key={f} data-testid={`payment-filter-${f}`} onClick={() => setFilter(f)} className={`px-3.5 py-1.5 rounded-full text-sm ${filter === f ? "bg-forest text-ivory" : "border border-charcoal/15 hover:bg-charcoal/5"}`}>{f}</button>
                ))}
                <input data-testid="payment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order ID…" className="ml-auto rounded-full border border-charcoal/15 px-4 py-1.5 text-sm bg-white" />
            </div>

            <div className="overflow-x-auto card-soft">
                <table className="w-full text-sm" data-testid="payments-table">
                    <thead className="text-left text-charcoal/55 border-b border-charcoal/10">
                        <tr>{["Order", "Customer", "Amount", "Method", "Txn ref", "Submitted", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-charcoal/50">No orders found.</td></tr>}
                        {orders.map((o) => (
                            <tr key={o.order_id} className="border-b border-charcoal/5">
                                <td className="px-4 py-3 font-mono text-xs">{o.order_id}</td>
                                <td className="px-4 py-3">{o.customer?.name}<br /><span className="text-charcoal/50 text-xs">{o.customer?.email}</span></td>
                                <td className="px-4 py-3">₹{o.amount}</td>
                                <td className="px-4 py-3">{o.method || "—"}</td>
                                <td className="px-4 py-3 text-xs">{o.txn_ref || "—"}</td>
                                <td className="px-4 py-3 text-xs">{o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}</td>
                                <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs ${badge(o.status)}`}>{o.status}</span></td>
                                <td className="px-4 py-3">
                                    {["PENDING", "SUBMITTED"].includes(o.status) && (
                                        <div className="flex gap-2">
                                            <button data-testid={`approve-${o.order_id}`} onClick={() => setApproveOrder(o)} className="text-xs rounded-full bg-forest text-ivory px-3 py-1.5">Approve</button>
                                            <button data-testid={`reject-${o.order_id}`} onClick={() => setRejectOrder(o)} className="text-xs rounded-full border border-destructive text-destructive px-3 py-1.5">Reject</button>
                                        </div>
                                    )}
                                    {o.status === "REJECTED" && o.reject_reason && <span className="text-xs text-charcoal/50">{o.reject_reason}</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Approve confirm */}
            <AlertDialog open={!!approveOrder} onOpenChange={(o) => !o && setApproveOrder(null)}>
                <AlertDialogContent className="bg-white" data-testid="approve-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Have you independently verified that this payment was received?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Order {approveOrder?.order_id} · ₹{approveOrder?.amount} · Ref {approveOrder?.txn_ref || "—"}.<br />
                            Only approve after confirming the money is actually in your bank/UPI account. A screenshot or reference is not proof.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid="confirm-approval" onClick={doApprove} disabled={busy} className="bg-forest text-ivory">Confirm Approval</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reject dialog */}
            <Dialog open={!!rejectOrder} onOpenChange={(o) => !o && setRejectOrder(null)}>
                <DialogContent className="bg-white" data-testid="reject-dialog">
                    <DialogHeader><DialogTitle>Reject payment {rejectOrder?.order_id}</DialogTitle></DialogHeader>
                    <textarea data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (shared with the customer)…"
                        className="w-full rounded-xl border border-charcoal/15 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-forest/30 text-sm" />
                    <DialogFooter>
                        <button onClick={() => setRejectOrder(null)} className="px-4 py-2 text-sm">Cancel</button>
                        <button data-testid="confirm-reject" onClick={doReject} disabled={busy} className="px-5 py-2 rounded-full bg-destructive text-white text-sm">Reject</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Customers() {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get("/admin/customers").then((r) => setRows(r.data.customers)).catch(() => {}); }, []);
    return (
        <div className="overflow-x-auto card-soft">
            <table className="w-full text-sm">
                <thead className="text-left text-charcoal/55 border-b border-charcoal/10"><tr>{["Name", "Email", "Registered", "Lang", "Answered", "Order", "Memory"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-charcoal/5">
                            <td className="px-4 py-3">{r.name}</td>
                            <td className="px-4 py-3 text-charcoal/60">{r.email}</td>
                            <td className="px-4 py-3 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                            <td className="px-4 py-3">{r.language}</td>
                            <td className="px-4 py-3">{r.answered}/18</td>
                            <td className="px-4 py-3">{r.order_status || "—"}</td>
                            <td className="px-4 py-3">{r.memory_consent ? "on" : "off"}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-charcoal/50">No customers yet.</td></tr>}
                </tbody>
            </table>
        </div>
    );
}

function Analytics() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat label="Started" value={d.started} />
            <Stat label="Free completed" value={d.free_completed} />
            <Stat label="Completed (report)" value={d.completed} />
            <Stat label="Abandoned" value={d.abandoned} />
            <Stat label="Free → paid conversion" value={`${d.free_to_paid_conversion}%`} />
            <Stat label="Report gen failures" value={d.report_generation_failures} accent={d.report_generation_failures ? "text-destructive" : "text-forest"} />
        </div>
    );
}

function AIUsage() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/ai-usage").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Stat label="Total requests" value={d.total} />
                <Stat label="Errors" value={d.errors} accent={d.errors ? "text-destructive" : "text-forest"} />
                <Stat label="Preview / Report" value={`${d.by_kind.preview} / ${d.by_kind.report}`} />
                <Stat label="Companion" value={d.by_kind.companion} />
            </div>
            <p className="text-xs text-charcoal/50 mb-3">Cost estimate is not available without provider billing data. Request counts are tracked locally.</p>
            <div className="card-soft overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-charcoal/55 border-b border-charcoal/10"><tr>{["Kind", "OK", "When", "Detail"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>{d.recent.map((r, i) => (<tr key={i} className="border-b border-charcoal/5"><td className="px-4 py-2">{r.kind}</td><td className="px-4 py-2">{r.ok ? "✓" : "✗"}</td><td className="px-4 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td><td className="px-4 py-2 text-xs text-charcoal/50">{r.detail}</td></tr>))}</tbody></table></div>
        </div>
    );
}

function Support() {
    const [rows, setRows] = useState([]);
    const load = () => api.get("/admin/support").then((r) => setRows(r.data.support)).catch(() => {});
    useEffect(() => { load(); }, []);
    const resolve = async (id, status) => { await api.patch(`/admin/support/${id}`, { status }); load(); };
    return (
        <div className="space-y-3">
            {rows.length === 0 && <p className="text-charcoal/50 text-sm">No support requests.</p>}
            {rows.map((r) => (
                <div key={r.req_id} className="card-soft p-5">
                    <div className="flex items-center justify-between"><p className="font-medium">{r.name} <span className="text-charcoal/50 text-sm">· {r.email}</span></p>
                        <span className={`text-xs px-2.5 py-1 rounded-full ${r.status === "open" ? "bg-gold/15 text-gold" : "bg-forest/10 text-forest"}`}>{r.status}</span></div>
                    <p className="mt-2 text-sm text-charcoal/75">{r.message}</p>
                    <div className="mt-3 flex gap-2">
                        <button onClick={() => resolve(r.req_id, "resolved")} className="text-xs rounded-full bg-forest text-ivory px-3 py-1.5">Mark resolved</button>
                        <button onClick={() => resolve(r.req_id, "open")} className="text-xs rounded-full border border-charcoal/15 px-3 py-1.5">Reopen</button>
                    </div>
                </div>
            ))}
        </div>
    );
}

function Privacy() {
    const [d, setD] = useState(null);
    useEffect(() => { api.get("/admin/privacy").then((r) => setD(r.data)).catch(() => {}); }, []);
    if (!d) return <Loading />;
    return (
        <div>
            <div className="flex items-center gap-2 mb-4 text-sm text-charcoal/70"><Shield size={16} /> Memory consents active: <strong className="text-forest">{d.memory_consents}</strong></div>
            <div className="card-soft overflow-x-auto"><table className="w-full text-sm"><thead className="text-left text-charcoal/55 border-b border-charcoal/10"><tr>{["Type", "User", "Email", "When"].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr></thead>
                <tbody>{d.requests.map((r, i) => (<tr key={i} className="border-b border-charcoal/5"><td className="px-4 py-2">{r.type}</td><td className="px-4 py-2 text-xs">{r.user_id}</td><td className="px-4 py-2 text-xs">{r.email}</td><td className="px-4 py-2 text-xs">{new Date(r.created_at).toLocaleString()}</td></tr>))}
                    {d.requests.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-charcoal/50">No privacy requests.</td></tr>}</tbody></table></div>
        </div>
    );
}

const Loading = () => <div className="py-16 flex justify-center"><div className="h-7 w-7 rounded-full border-2 border-forest/20 border-t-forest animate-spin" /></div>;
